import { useQuery } from "react-query";
import { deleteDecisionChunk, getDecisionChunks } from "../../../api/decision/decision";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { StyledDirectionTableContainer, StyledDirectionTableHeader } from "../../../styles/StyledDecision/StyledContent";
import { Button } from "../../Button";
import { QueryLoader } from "../../Loader";
import AddIcon from "../../../assets/img/icons/add.svg";
import { useState } from "react";
import Modal from "react-modal";
import { StyledModal, StyledPointsModal } from "../../../styles/StyledModal";
import { ModalHeader } from "../../ModalHeader";
import { AddChunk } from "./AddChunk";
import { DecisionChunk } from "./DecisionChunk";
import type { DecisionChunk as DecisionChunkType } from "./DecisionChunk";
import { ConfirmationModal } from "../../ConfirmationModal";

type DecisionChunksProps = {
	id?: string;
	newDecision?: boolean;
};

export const DecisionChunks = (props: DecisionChunksProps) => {
	const { id, newDecision = false } = props;
	const { axiosPrivate } = useAxiosPrivate();
	
	// For new decisions, don't make API calls until we have a valid ID
	const shouldFetchData = !newDecision && Boolean(id);
	const { isLoading, isError, error, data, refetch } = useQuery<DecisionChunkType[]>(
		["decision-chunks", id], 
		() => getDecisionChunks(axiosPrivate, id!),
		{ enabled: shouldFetchData }
	);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteId, setDeleteId] = useState<number | undefined>(undefined);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editId, setEditId] = useState<number | undefined>(undefined);

	const handleDelete = async () => {
		if (!id) return false;
		await deleteDecisionChunk(axiosPrivate, id, deleteId, () => {
			setIsDeleteModalOpen(false);
			refetch();
		});
		return true;
	};

	// For new decisions, show the interface without loading
	if (newDecision) {
		return (
			<>
				<StyledDirectionTableHeader>
					<span>Order</span>
					<span>Title</span>
					<span>Label</span>
					<span>Actions</span>
				</StyledDirectionTableHeader>
				<StyledDirectionTableContainer>
					<h6>No addressee part added (save decision first to add chunks)</h6>
				</StyledDirectionTableContainer>
			</>
		);
	}

	if (isLoading) {
		return <QueryLoader />;
	}
	if (isError) {
		console.error(error);
		return <div>Something went wrong...</div>;
	}

	return (
		<>
			<StyledDirectionTableHeader>
				<span>Order</span>
				<span>Title</span>
				<span>Label</span>
				<span>Actions</span>
			</StyledDirectionTableHeader>
			<StyledDirectionTableContainer>
				{data && data.length > 0 ? (
					[...data]
						.sort((a, b) => a.order - b.order)
						.map((chunk) => (
						<DecisionChunk
							key={chunk.id}
							chunk={chunk}
							setDeleteId={(id: number) => setDeleteId(id)}
							openDeleteModal={() => setIsDeleteModalOpen(true)}
							setEditId={(id: number) => setEditId(id)}
							openEditModal={() => setIsEditModalOpen(true)}
						/>
						))
					) : (
					<h6>No addressee part added</h6>
				)}
				<Button 
					icon={AddIcon} 
					onClick={() => setIsAddModalOpen(true)}
					alabel="Add addressee part"
					testid="add-chunk-button"
					initial={false}
				>
					Add addressee part
				</Button>
			</StyledDirectionTableContainer>
			{id && (
				<Modal
					isOpen={isAddModalOpen}
					onRequestClose={() => {
						setIsAddModalOpen(false);
					}}
					style={StyledPointsModal}
					ariaHideApp={true}
				>
					<ModalHeader
						onClick={() => {
							setIsAddModalOpen(false);
						}}
					>
						Add Decision Chunk
					</ModalHeader>
					<AddChunk id={id} closeModal={() => setIsAddModalOpen(false)} refetch={refetch} chunk={undefined} totalChunks={data?.length ?? 0} />
				</Modal>
			)}
			{id && (
				<>
					<Modal
						isOpen={isDeleteModalOpen}
						onRequestClose={() => {
							setDeleteId(undefined);
							setIsDeleteModalOpen(false);
						}}
						style={StyledModal}
						ariaHideApp={true}
					>
						<ConfirmationModal
							handleDelete={handleDelete}
							deletedId={deleteId}
							closeModal={() => {
								setDeleteId(undefined);
								setIsDeleteModalOpen(false);
							}}
						/>
					</Modal>
					<Modal
						isOpen={isEditModalOpen}
						onRequestClose={() => {
							setIsEditModalOpen(false);
							setEditId(undefined);
						}}
						style={StyledPointsModal}
						ariaHideApp={true}
					>
						<ModalHeader
							onClick={() => {
								setIsEditModalOpen(false);
								setEditId(undefined);
							}}
						>
							Edit Decision Chunk
						</ModalHeader>
						<AddChunk
							id={id}
							closeModal={() => {
								setIsEditModalOpen(false);
								setEditId(undefined);
							}}
							refetch={refetch}
							chunk={data?.find((chunk: any) => chunk.id === editId)}
							totalChunks={data?.length ?? 0}
						/>
					</Modal>
				</>
			)}
		</>
	);
};
